<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

// Define props
const props = defineProps({
    open: { type: Boolean, required: true },
    activity_logs: { type: Array, required: false }
})

// Define a close emit
defineEmits(['close'])

// Manage expanded state for logs
const expandedLogs = ref([])
const page = usePage()

const fieldLabels = {
    currency_id: 'Currency',
    buy_currency_id: 'Buy Currency',
    sell_currency_id: 'Sell Currency',
    fixture_type_id: 'Fixture Type',
    client_id: 'Client',
    trading_details_id: 'Trading Name',
    user_id: 'Travel Manager'
}

const currencyLookup = computed(() => {
    const currencies = page.props.currencies

    if (!Array.isArray(currencies)) {
        return {}
    }

    return currencies.reduce((lookup, currency) => {
        lookup[currency.id] = `${currency.name} (${currency.code})`
        return lookup
    }, {})
})

const formatFieldLabel = (field) => {
    if (!field) {
        return 'Field'
    }

    return fieldLabels[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

const formatValue = (field, value) => {
    if (value === null || value === undefined || value === '') {
        return 'N/A'
    }

    if (['currency_id', 'buy_currency_id', 'sell_currency_id'].includes(field)) {
        return currencyLookup.value[value] ?? value
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
    }

    return value
}

const normalizeChanges = (log) => {
    if (Array.isArray(log.changes)) {
        return log.changes.map((change) => ({
            field: change.field,
            label: change.label ?? formatFieldLabel(change.field),
            old_value: formatValue(change.field, change.old_value),
            new_value: formatValue(change.field, change.new_value)
        }))
    }

    const attributes = log.properties?.attributes ?? {}
    const oldValues = log.properties?.old ?? {}

    return Object.entries(attributes).map(([field, newValue]) => ({
        field,
        label: formatFieldLabel(field),
        old_value: formatValue(field, oldValues[field]),
        new_value: formatValue(field, newValue)
    }))
}

const getCauser = (log) => {
    if (log.causer) {
        return log.causer
    }

    const firstName = log.user?.first_name ?? ''
    const lastName = log.user?.last_name ?? ''
    const fullName = `${firstName} ${lastName}`.trim()

    return fullName || 'N/A'
}

const normalizedLogs = computed(() => {
    if (!Array.isArray(props.activity_logs)) {
        return []
    }

    return props.activity_logs.map((log) => ({
        ...log,
        title: log.title ?? log.event ?? 'Updated',
        changes: normalizeChanges(log),
        causer_name: getCauser(log)
    }))
})

// Toggle expanded state for a specific log
const toggleExpanded = (index) => {
    expandedLogs.value[index] = !expandedLogs.value[index]
}
</script>

<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-10" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-lg font-semibold text-gray-900">
                                        Activity Log
                                    </DialogTitle>
                                    <p class="mt-1 text-sm text-gray-500">Logs are listed from newest to oldest.</p>
                                    <hr class="my-5"/>
                                    <div class="text-left max-h-96 overflow-auto">
                                        <ul class="divide-y divide-gray-200" v-if="normalizedLogs.length > 0">
                                            <li v-for="(log, index) in normalizedLogs" :key="log.id ?? index" class="py-4">
                                                <div class="text-sm font-medium text-gray-900 capitalize">
                                                    <strong>{{ log.description || log.title }}</strong>
                                                </div>
                                                <div class="mt-2 text-sm text-gray-700" v-if="log.changes.length > 0">
                                                    <p>
                                                        <strong>Updated Fields:</strong>
                                                    </p>
                                                    <ul class="ml-4 mt-2 list-disc">
                                                        <li v-for="(change, idx) in log.changes" v-show="idx < 3 || expandedLogs[index]" :key="`${change.field}-${idx}`">
                                                            <strong>{{ change.label }}:</strong>
                                                            <span class="text-gray-500 line-through">{{ change.old_value }}</span>
                                                            →
                                                            <span class="text-green-600">{{ change.new_value }}</span>
                                                        </li>
                                                    </ul>
                                                    <div v-if="log.changes.length > 3" class="mt-2">
                                                        <button type="button" class="text-xs text-indigo-600 hover:underline" @click="toggleExpanded(index)">
                                                            <strong>{{ expandedLogs[index] ? 'Show Less' : 'Show More' }}</strong>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="mt-2 text-xs text-gray-500">
                                                    <p>
                                                        <strong>Event:</strong> {{ log.event }}
                                                    </p>
                                                    <p>
                                                        <strong>Updated By:</strong> {{ log.causer_name }}
                                                    </p>
                                                    <p>
                                                        <strong>Timestamp:</strong>
                                                        {{ new Date(log.created_at).toLocaleString() }}
                                                    </p>
                                                </div>
                                            </li>
                                        </ul>
                                        <div class="text-center" v-else>
                                            <p class="mt-1 text-sm text-gray-500">No Logs.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-6 w-full">
                                <button type="button"
                                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                    @click="$emit('close')" ref="cancelButtonRef">close</button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
